import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, forkJoin, map, mergeMap, Observable, of, switchMap } from 'rxjs';
import { Action } from '@ngrx/store';
import * as PupilDuplicateActions from './pupil-duplicate.actions';
import { PupilDuplicateService } from '@core/services/pupil-duplicate.service';
import { AccountService } from '@core/services/account.service';
import { extractPupilLinkErrorMessage } from '../pupil-link/pupil-link.effects';

@Injectable()
export class PupilDuplicateEffects {
  constructor(
    private actions$: Actions,
    private pupilDuplicateService: PupilDuplicateService,
    private accountService: AccountService
  ) {}

  loadGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PupilDuplicateActions.loadPupilDuplicates),
      switchMap(() =>
        this.pupilDuplicateService.loadGroups().pipe(
          map((groups) => PupilDuplicateActions.loadPupilDuplicatesSuccess({ groups })),
          catchError((error) => of(PupilDuplicateActions.loadPupilDuplicatesFailure({ error: extractPupilLinkErrorMessage(error) })))
        )
      )
    )
  );

  dismissGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PupilDuplicateActions.dismissPupilDuplicate),
      mergeMap(({ groupId }) =>
        this.pupilDuplicateService.dismiss(groupId).pipe(
          map((id) => PupilDuplicateActions.dismissPupilDuplicateSuccess({ groupId: id })),
          catchError((error) => of(PupilDuplicateActions.dismissPupilDuplicateFailure({
            groupId,
            error: extractPupilLinkErrorMessage(error)
          })))
        )
      )
    )
  );

  /**
   * One remove-user call per copy. A copy that fails does not undo the others, so the ones that went
   * through still leave the page, and the first failure is reported.
   */
  removeMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PupilDuplicateActions.removePupilDuplicateMembers),
      mergeMap(({ groupId, accountIds }) =>
        forkJoin(accountIds.map((accountId) =>
          this.accountService.removeUser(accountId).pipe(
            map(() => ({ accountId, error: null as any })),
            catchError((error) => of({ accountId, error: extractPupilLinkErrorMessage(error) }))
          )
        )).pipe(
          switchMap((results): Observable<Action> => {
            const removed = results.filter((result) => !result.error).map((result) => result.accountId);
            const failed = results.find((result) => !!result.error);
            const actions: Action[] = [];
            if (removed.length) {
              actions.push(PupilDuplicateActions.removePupilDuplicateMembersSuccess({ groupId, accountIds: removed }));
            }
            if (failed) {
              actions.push(PupilDuplicateActions.removePupilDuplicateMembersFailure({ groupId, error: failed.error }));
            }
            return of(...actions);
          })
        )
      )
    )
  );
}
